import React from 'react';
import {
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Avatar,
  Typography,
  Badge,
  IconButton,
  Tooltip,
  Box,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import { 
  PersonAdd as PersonAddIcon,
  Star as StarIcon
} from '@mui/icons-material';
import UserStatus from './UserStatus';
import api from '../../utils/api';
import '../../styles/custom.css';

const ContactList = ({ contacts, selectedContact, onSelectContact, onlineUsers = [], onSendFriendRequest }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSendFriendRequest = async (contactId, event) => {
    event.stopPropagation();
    try {
      await api.post(`/users/friend-request/${contactId}`);
      if (onSendFriendRequest) {
        onSendFriendRequest(contactId);
      }
    } catch (err) {
      console.error('Error sending friend request:', err);
    }
  };

  const renderLastMessage = (contact) => {
    if (!contact.lastMessage) return null;
    
    const messagePreview = contact.lastMessage.media 
      ? `[${contact.lastMessage.media.type === 'image' ? 'Photo' : 'Video'}]` 
      : contact.lastMessage.content;

    return (
      <Typography
        variant="body2"
        color="text.secondary"
        component="span"
        sx={{
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: isMobile ? '180px' : '200px',
          fontSize: isMobile ? '0.8rem' : '0.875rem',
          opacity: 0.7
        }}
      >
        {messagePreview}
      </Typography>
    );
  };

  return (
    <List 
      className="contact-list"
      sx={{ 
        width: '100%', 
        bgcolor: 'background.paper',
        p: 0,
        borderRadius: { xs: 0, sm: 2 },
        overflow: 'hidden',
        '& .MuiListItem-root': {
          transition: 'all 0.3s ease',
        }
      }}
    >
      {contacts.map((contact, index) => (
        <React.Fragment key={contact._id}>
          <ListItem
            component="div"
            className="contact-item"
            sx={{
              cursor: 'pointer',
              py: isMobile ? 1.5 : 2,
              px: 2,
              position: 'relative',
              overflow: 'hidden',
              bgcolor: selectedContact?._id === contact._id 
                ? theme.palette.mode === 'dark'
                  ? 'action.selected'
                  : 'primary.lighter'
                : 'transparent',
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark'
                  ? 'action.hover'
                  : 'primary.lightest',
              },
              '&.Mui-selected': {
                bgcolor: theme.palette.mode === 'dark'
                  ? 'action.selected'
                  : 'primary.lighter',
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark'
                    ? 'action.selected'
                    : 'primary.light',
                }
              }
            }}
            selected={selectedContact?._id === contact._id}
            onClick={() => onSelectContact(contact)}
          >
            <ListItemAvatar>
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                variant="dot"
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: onlineUsers.includes(contact._id) ? '#44b700' : 'grey.500',
                    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
                    '&::after': {
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      animation: onlineUsers.includes(contact._id) ? 'ripple 1.2s infinite ease-in-out' : 'none',
                      border: '1px solid currentColor',
                      content: '""',
                    },
                  },
                }}
              >
                <Avatar 
                  src={contact.avatar} 
                  alt={contact.username}
                  className={onlineUsers.includes(contact._id) ? 'status-online' : 'status-offline'}
                  sx={{ 
                    width: isMobile ? 44 : 52, 
                    height: isMobile ? 44 : 52,
                    bgcolor: theme.palette.mode === 'dark'
                      ? 'primary.main'
                      : 'primary.dark',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.05)'
                    }
                  }}
                >
                  {contact.username[0].toUpperCase()}
                </Avatar>
              </Badge>
            </ListItemAvatar>
            <ListItemText
              sx={{ zIndex: 1 }}
              primary={
                <Typography 
                  variant={isMobile ? "body1" : "subtitle1"} 
                  component="span"
                  sx={{ 
                    fontWeight: selectedContact?._id === contact._id ? 600 : 500,
                    color: 'text.primary',
                    transition: 'color 0.3s ease'
                  }}
                >
                  {contact.username}
                </Typography>
              }
              secondary={
                <Box component="span" sx={{ display: 'block' }}>
                  <Typography 
                    component="span" 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      display: 'block',
                      fontSize: isMobile ? '0.75rem' : '0.8rem',
                      mb: 0.5
                    }}
                  >
                    <UserStatus
                      lastSeen={contact.lastSeen}
                      isOnline={onlineUsers.includes(contact._id)}
                    />
                  </Typography>
                  {renderLastMessage(contact)}
                </Box>
              }
            />
            {!contact.isFriend && (
              <ListItemSecondaryAction>
                <Tooltip title="Send Friend Request">
                  <IconButton
                    edge="end"
                    onClick={(e) => handleSendFriendRequest(contact._id, e)}
                    sx={{ 
                      color: theme.palette.mode === 'dark'
                        ? 'primary.light'
                        : 'primary.main'
                    }}
                  >
                    <PersonAddIcon />
                  </IconButton>
                </Tooltip>
              </ListItemSecondaryAction>
            )}
          </ListItem>
          {index < contacts.length - 1 && (
            <Divider 
              variant="inset" 
              component="li"
              sx={{
                borderColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.08)'
              }}
            />
          )}
        </React.Fragment>
      ))}
    </List>
  );
};

export default ContactList; 